import React, { useState } from "react";
import {Alert, Col, Form, Modal, Row} from "antd";
import { useTranslation } from "react-i18next";
import { useRequiredLayers } from "../../../../utils/context";
import { CartCreateStore, CartStoreContext } from "../cart.create.store";
import { CartCreateController, CartCreateControllerContext } from "../cart.create.controller";
import { ErrorResponseData } from "../../../../models/core/response";
import { AxiosError } from "axios";
import { DebounceSelect } from "../../../core/commons/debounce-select/debounce.select";
import { NumberInput } from "../../../core/commons/number-input/number.input";
import { createArticleRequest } from "../../../../models/cart/helper/cart.helper";

interface CartCreateArticleModalProps {
    onClose: () => void;
}

export const CartCreateArticleModal: React.FC<CartCreateArticleModalProps> = ({ onClose }) => {
    const { controller } = useRequiredLayers<CartCreateStore, CartCreateController>(
        CartStoreContext,
        CartCreateControllerContext
    );

    const { t } = useTranslation("translation", { useSuspense: false });
    const [form] = Form.useForm();
    const [loading, setLoading] = useState<boolean>(false);

    const [articles, setArticles] = useState<any>([]);
    const [selectedArticle, setSelectedArticle] = useState<any>();

    return (
        <Modal
            visible={true}
            maskClosable={false}
            confirmLoading={loading}
            title={t("CART.ADD_ROW")}
            okText={t("ADD")}
            cancelText={t("CANCEL")}
            onOk={() => form.submit()}
            onCancel={() => {
                onClose();
                form.resetFields();
            }}
        >
            <Form
                size={"middle"}
                form={form}
                layout="vertical"
                name={"add_to_cart_form"}
                onFinish={() =>
                    form.validateFields().then((values) => {
                        setLoading(true);
                        controller
                            .addToCart(createArticleRequest(values))
                            .then(
                                () => {
                                    form.resetFields();
                                    return onClose();
                                },
                                (e: AxiosError<ErrorResponseData>) => {
                                    setLoading(false);
                                    if (e.response?.data.fields) {
                                        form.setFields(e.response?.data?.fields);
                                    }
                                }
                            )
                            .then(() => (e: any) => form.setFields(e.response?.data?.fields))
                            .finally(() => setLoading(false));
                    })
                }
            >
                <Row gutter={[10, 0]} align={"middle"}>
                    <Col span={24}>
                        <Form.Item
                            name={"article"}
                            label={t("ARTICLES.SEARCH")}
                            rules={[
                                {
                                    required: true,
                                    message: t("FROM.ERROR.PLEASE_SELECT_VALUE"),
                                },
                            ]}
                        >
                            <DebounceSelect
                                autoFocus={true}
                                placeholder={t("ARTICLES.SEARCH")}
                                style={{ width: "100%" }}
                                disabled={loading}
                                fetchOptions={controller.searchArticles}
                                onSelect={v => setSelectedArticle(articles.find((a: any) => a.key = v.value))}
                                onOptionsUpdated={setArticles}
                            />
                        </Form.Item>
                    </Col>
                    <Col span={24}>
                        <Form.Item
                            name={"qty"}
                            label={t("QUANTITY")}
                            rules={[
                                {
                                    required: true,
                                    message: t("FROM.ERROR.PLEASE_SELECT_VALUE"),
                                },
                            ]}
                            initialValue={1}
                        >
                            <NumberInput disabled={loading} />
                        </Form.Item>
                        {selectedArticle?.multiple > 1 ? (
                            <Alert
                                message={t('ARTICLE.WITH_MULTIPLIER.DESCRIPTION', {multiple: selectedArticle.multiple})}
                                type="info"
                                showIcon
                            />
                        ) : null}
                    </Col>
                </Row>
            </Form>
        </Modal>
    );
};
